import { useState } from 'react'
import { Box } from '@mui/material'
import { ApartmentOutlined } from '@mui/icons-material'
import { SwitchTenantCardProps } from './SwitchTenantCard.types'
import { Button } from './Button'

/**
 * The one screen before the application: pick a tenant (design system:
 * TenantCard). A 420px card: title, a listbox of tenants, Cancel and a
 * primary Continue that stays disabled until the choice differs from the
 * current tenant.
 *
 * Rows are options in a `listbox`, reachable by Tab and selected with Enter
 * or Space. The zebra striping is gone; the hairlines are enough and the
 * stripe fought the selected row.
 */
export const SwitchTenantCard = ({
  title,
  tenantList,
  initialTenantId = '',
  primaryButtonLabel,
  secondaryButtonLabel,
  showSecondaryButton = true,
  handlePrimaryButtonClick,
  handleSecondaryButtonClick,
}: SwitchTenantCardProps) => {
  const [selectedTenantId, setSelectedTenantId] =
    useState<string>(initialTenantId)

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    tenantId: string
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setSelectedTenantId(tenantId)
    }
  }

  const shouldDisableButton = () => {
    return !selectedTenantId || selectedTenantId === initialTenantId
  }

  return (
    <Box
      component="section"
      aria-labelledby="tenant-card-title"
      sx={{
        width: 420,
        maxWidth: '100%',
        background: 'var(--surface-0)',
        border: '1px solid var(--line-200)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-panel)',
        overflow: 'hidden',
      }}
    >
      <Box
        component="h1"
        id="tenant-card-title"
        sx={{
          m: 0,
          px: '16px',
          py: '14px',
          fontSize: 16,
          fontWeight: 700,
          letterSpacing: '-0.005em',
          color: 'var(--brand-teal)',
          borderBottom: '1px solid var(--line-200)',
        }}
      >
        {title}
      </Box>

      <Box
        component="ul"
        role="listbox"
        aria-labelledby="tenant-card-title"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          maxHeight: 360,
          overflowY: 'auto',
        }}
      >
        {tenantList.map((tenant) => {
          const isSelected = tenant.id === selectedTenantId
          return (
            <Box
              component="li"
              key={tenant.id}
              role="option"
              aria-selected={isSelected}
              tabIndex={0}
              onClick={() => setSelectedTenantId(tenant.id)}
              onKeyDown={(e) => handleKeyDown(e, tenant.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                px: '16px',
                py: '9px',
                fontSize: 13,
                cursor: 'pointer',
                borderBottom: '1px solid var(--line-100)',
                color: isSelected ? 'var(--brand-blue-600)' : 'var(--ink-700)',
                fontWeight: isSelected ? 600 : 400,
                background: isSelected ? 'var(--brand-blue-50)' : 'transparent',
                '&:hover': {
                  background: isSelected
                    ? 'var(--brand-blue-50)'
                    : 'var(--surface-50)',
                },
                '&:focus-visible': { outlineOffset: '-2px' },
              }}
            >
              <ApartmentOutlined
                sx={{
                  fontSize: 15,
                  color: isSelected
                    ? 'var(--brand-blue-600)'
                    : 'var(--ink-400)',
                }}
              />
              {tenant.name}
            </Box>
          )
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          px: '16px',
          py: '12px',
        }}
      >
        {showSecondaryButton && (
          <Button
            variant="outlined"
            onClick={() => handleSecondaryButtonClick()}
          >
            {secondaryButtonLabel}
          </Button>
        )}
        <Button
          variant="contained"
          disabled={shouldDisableButton()}
          onClick={() => handlePrimaryButtonClick(selectedTenantId)}
        >
          {primaryButtonLabel}
        </Button>
      </Box>
    </Box>
  )
}
